// AllocationSummaryCard — 사용자 요청 (2026-05-14): LicenseDealerTab 의 Subsidiary 분기
// 와 Dealer 분기에서 동일한 "Allocation Summary" 카드가 두 곳에 복제되어 있어 같은
// 출력이 조직 타입별로 분기되는 패턴 해소. 카드 본체를 단일 컴포넌트로 추출.
//
// 외부 wrapper (예: Dealer 의 row/col 그리드, Subsidiary 의 mb-3) 차이는 호출처 책임.
// 본 컴포넌트는 카드 본체 (border + body + 배지 + 잔여량 라인 + 버튼 묶음) 만 렌더.
// (제목은 두지 않는다 — 요약 제목 규칙은 License Hub 배치 통일 기준 3번)

import React from 'react';
import { Translate as t } from '../../../config/Translate';
import { buildSummaryBadges } from '../../../lib/smartDentQuota';
import { SUMMARY_BG, SUMMARY_HIGHLIGHT_BG } from '../../../components/license/summaryStyle';

// IMP-224 (PR #240 review): testid prop override 지원 — 같은 컴포넌트가 두 분기
// (Subsidiary / Dealer) 에서 렌더되어도 RTL 테스트에서 위치별 식별 가능.
// 기본값은 호환성 유지 위해 `allocation-summary-card`.
// IMP-392: onRefresh (optional) — 전달 시 헤더에 리프레쉬 버튼 노출 (LicenseSubsidiaryTab
// 인라인 리프레쉬 버튼과 동일 스타일). 미전달 시 기존과 동일(버튼 없음, 무회귀).
// IMP-389: selfDealing (optional) — true 시 self-dealing 정보성 서브텍스트 노출.
//
// IMP-427 (#368): 원천 수치 배지 추가. 종전에는 잔여 한 줄만 노출해 그 값이 어떤 수치에서
// 나왔는지 화면에서 검산할 수 없었다. 신규 prop `total` / `allocated` / `redeemed`
// (`{ newQty, renewQty }`) 는 전부 optional 이며 미전달 시 기존과 동일 렌더(무회귀).
//
// [중요] 배지는 `Total − Allocated − Redeemed = 잔여 줄 값` 뺄셈이 성립하는 수치만 담는다.
// 따라서 `redeemed` 에는 **표시 remaining 이 실제로 차감하는 자기 리딤**만 넘겨야 한다 —
// 하위 조직 리딤까지 합친 조직 전체 롤업(최상위 법인의 corp 원장 2-seg `u`)을 넘기면 이미
// `allocated` 에 포함된 분을 두 번 빼는 셈이 되어 화면 잔여 줄과 어긋난다(BUG-105 계열).
// 롤업이 필요하면 `orgWideRedeemed` (optional) 로 넘긴다 — 배지가 아닌 참고 줄로 렌더된다.
//
// 발급 누적(`i`)은 **표시하지 않는다** — 사용자 확정(2026-07-31): `i > t` 가 정상(마케팅 초과
// 발급)이라 뺄셈에 들어갈 수 없고, 배지 옆에 두면 "왜 안 빠지나" 혼선만 유발한다.
// (prd-rayguard/docs/specs/quota-glossary.md 가 용어 SSoT)
const AllocationSummaryCard = ({
    remaining,
    className = '',
    testid = 'allocation-summary-card',
    onRefresh,
    selfDealing = false,
    total,
    allocated,
    redeemed,
    orgWideRedeemed,
    actions = null,
    // 라벨은 호출처가 remaining 값을 고르는 분기와 같은 조건으로 넘긴다(용어집 §2).
    // 기본값 = 딜러 자기 레코드 t−u = Redeemable Remainder.
    remainingLabelKey = 'license.label.redeemableRemainder',
}) => {
    const newQty = remaining?.newQty ?? 0;
    const renewQty = remaining?.renewQty ?? 0;
    // h-100: 옆 PC 활성화 카드와 높이를 맞추고, 버튼 묶음을 카드 맨 아래(mt-auto)에 붙인다.
    const composedClassName = `card h-100${className ? ` ${className}` : ''}`;

    // 배지 조립은 lib SSoT 경유 — LicenseSubsidiaryTab(인라인 카드)과 마크업·키가 갈리지 않게
    // 한다(PR #540 리뷰 M5: 종전 복제 상태에서 이미 드리프트 발생). 값이 전달된 항목만 렌더되고
    // 0 은 유효값이므로 falsy 누락 없이 표시된다.
    const badges = buildSummaryBadges({ total, allocated, redeemed });

    return (
        <div className={composedClassName} data-testid={testid} style={{ background: SUMMARY_BG }}>
            <div className="card-body p-0 d-flex flex-column">
                {/* 세로 쌓기: 배지 / 잔여 / 참고 줄 / 버튼 묶음(맨 아래) — 높이를 써서 가로 폭을 줄인다. */}
                <div className="px-3 py-2">
                    {/* IMP-427: 원천 수치 배지 — 잔여가 어떤 수치에서 나왔는지 먼저 보여준다.
                        전달된 항목만 렌더하므로 딜러(하위 배분 없음)는 자동으로 2개가 된다. */}
                    {badges.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mb-1" data-testid={`${testid}-badges`}>
                            {badges.map((b) => (
                                <span
                                    key={b.key}
                                    className="badge bg-light text-dark border fw-normal"
                                    data-testid={`${testid}-badge-${b.key}`}
                                >
                                    {t(b.labelKey)} {b.qty.newQty ?? 0} / {b.qty.renewQty ?? 0}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="mb-0 px-2 rounded" style={{ background: SUMMARY_HIGHLIGHT_BG }}>
                        {t(remainingLabelKey)}: {t('license.label.initial')} <strong>{newQty}</strong> / {t('license.label.renewal')} <strong>{renewQty}</strong>
                    </div>
                </div>
                {/* IMP-427: 조직 전체 리딤(하위 조직 포함) 참고 줄 — 이미 `allocated` 에 포함된 분을
                    담고 있어 위 뺄셈에 넣으면 이중 차감이 된다. 배지가 아니라 참고 줄로 둔다. */}
                {orgWideRedeemed && (
                    <small className="text-muted d-block px-3 py-1 border-top" data-testid={`${testid}-redeemed-orgwide-ref`}>
                        {t('license.label.redeemedOrgWide')}: {t('license.label.initial')} {orgWideRedeemed.newQty ?? 0} / {t('license.label.renewal')} {orgWideRedeemed.renewQty ?? 0}
                    </small>
                )}
                {/* IMP-389: self-dealing(선택 딜러 === 로그인 법인 자기 자신) 안내 —
                    알람이 아닌 정보성 서브텍스트로 통합 (usableRemainingHint 패턴과 동일). */}
                {selfDealing === true && (
                    <small className="text-muted d-block px-3 py-1 border-top" data-testid="smartdent-self-dealing-notice-card">
                        {t('license.message.selfDealingNotice')}
                    </small>
                )}
                {/* 버튼 묶음: [새로고침][주요 동작(예: Issue Voucher)] — 카드 맨 아래 왼쪽(옆 PC 카드 버튼과 같은 선). */}
                {(onRefresh || actions) && (
                    <div className="d-flex flex-wrap gap-2 mt-auto px-3 pt-1 pb-2">
                        {onRefresh && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                style={{ margin: 0 }}
                                data-testid={`${testid}-refresh-btn`}
                                onClick={onRefresh}
                            >
                                {t('common.button.refresh')}
                            </button>
                        )}
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllocationSummaryCard;
